import { useEffect, useRef } from 'react'
import { Toggle } from '@highlight/ui'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { AnimatePresence, motion, useReducedMotion, type Transition } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { useMissingAudioPermissions } from '@renderer/hooks/settings/useMissingAudioPermissions'
import { screenRecordingActionLabel, systemAudioActionLabel } from '@renderer/hooks/settings/audio-permission-prompt'
import { MeetingModeNotice } from './meeting-mode-notice'
import { useHoursPaywallVisible } from './meeting-hours-paywall'
import type { MeetingViewModel } from '../use-meeting-view-model'

/** How long the "ready" confirmation holds before the mode notice takes over. */
const READY_HOLD_MS = 5000

/**
 * Where the post-grant confirmation flow stands this app session:
 * `idle` (nothing granted yet, or permissions missing again) → the ready
 * deadline (all granted just now — confirmation card holds until it) →
 * `done` (confirmation dismissed, the surface behaves as if permissions were
 * always fine). An atom rather than local state so navigating views mid-flow
 * doesn't restart or lose the confirmation; the hold is a deadline rather
 * than a phase so a remount mid-hold resumes the countdown instead of
 * stranding the card on screen (the unmount cleared its timer).
 */
const grantFlowAtom = atom<'idle' | 'done' | { readyUntil: number }>('idle')

// Exit faster than enter so the handoff reads as one gesture; the enter's
// strong ease-out makes the incoming card feel deliberate.
const swapEnterTransition: Transition = { duration: 0.2, ease: [0.23, 1, 0.32, 1] }
const swapExitTransition: Transition = { duration: 0.12, ease: 'easeOut' }

/**
 * Everything the Meeting View mounts above the chat input, in priority order:
 *
 * 1. Permission toggles, while mic or the runtime's far-side permission is
 *    KNOWN missing (all checks resolved).
 * 2. The "ready" confirmation, held for {@link READY_HOLD_MS} right after the
 *    last grant lands, then animated out.
 * 3. The meeting-mode notice, on the pre-brief Get Ready screen. Hidden while
 *    the out-of-hours paywall is up, which its "will automatically
 *    transcribe" promise would contradict.
 *
 * One component decides all three so no two can disagree and leave the slot
 * incorrectly empty. Toggling a permission on requests it natively; the hooks
 * handle denied → System Settings deeplinks and poll for the grant. The
 * footer slot's height transition carries the size change between states;
 * AnimatePresence carries the crossfade.
 */
export function MeetingAudioPermissionsSurface({ model }: { model: MeetingViewModel }) {
  const {
    needs,
    mic,
    systemAudio,
    screenRecording,
    isLoaded,
    micMissing,
    systemAudioMissing,
    screenRecordingMissing,
    nextPermission,
  } = useMissingAudioPermissions({ meetingAppName: model.meetingAppName })
  const isMissing = micMissing || systemAudioMissing || screenRecordingMissing
  const wasMissingRef = useRef(false)
  const grantFlow = useAtomValue(grantFlowAtom)
  const setGrantFlow = useSetAtom(grantFlowAtom)
  const shouldReduceMotion = useReducedMotion()

  useEffect(() => {
    // All flags read "missing" until the first native check resolves — don't
    // latch (or show) anything off that.
    if (!isLoaded) {
      return
    }
    if (isMissing) {
      wasMissingRef.current = true
      setGrantFlow('idle')
      return
    }
    if (!wasMissingRef.current) {
      return
    }
    // The last grant just landed: swap to the confirmation immediately and
    // stamp its deadline, then hand over to the mode notice.
    wasMissingRef.current = false
    setGrantFlow({ readyUntil: Date.now() + READY_HOLD_MS })
  }, [isLoaded, isMissing, setGrantFlow])

  // The hold timer, keyed on the deadline rather than owned by the granting
  // mount: a remount mid-hold (view navigation) schedules the remainder
  // instead of stranding the confirmation on screen after its timer was
  // cleared with the previous mount.
  useEffect(() => {
    if (typeof grantFlow !== 'object') {
      return
    }
    const id = setTimeout(() => setGrantFlow('done'), Math.max(0, grantFlow.readyUntil - Date.now()))
    return () => clearTimeout(id)
  }, [grantFlow, setGrantFlow])

  const isGetReady = model.isUpcomingWithoutAudioNote && !(model.brief?.brief ?? '').trim()
  const hoursPaywallVisible = useHoursPaywallVisible(model)

  // Nothing renders before the first native checks resolve — the flags read
  // "missing" until then, and mounting the notice first only to swap it for
  // the grant card is exactly the flash this gating exists to avoid.
  // `wasMissingRef` while still 'idle' covers the paint between the grant
  // landing and the effect advancing the flow — toggles hold rather than the
  // notice flashing for a frame.
  const surface = !isLoaded
    ? null
    : isMissing || (wasMissingRef.current && grantFlow === 'idle')
      ? 'toggles'
      : typeof grantFlow === 'object'
        ? 'ready'
        : isGetReady && !hoursPaywallVisible
          ? 'notice'
          : null

  const swap = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 4 },
        animate: { opacity: 1, y: 0, transition: swapEnterTransition },
        exit: { opacity: 0, y: -4, transition: swapExitTransition },
      }

  return (
    <AnimatePresence mode="wait" initial={false}>
      {surface === 'toggles' && (
        <motion.div key="toggles" {...swap}>
          <div className="bg-background corner-smooth mb-3 flex flex-col gap-3 rounded-2xl border p-3">
            <div className="flex flex-col gap-1.5">
              <p className="text-foreground type-ui-sm font-medium">Allow Highlight to understand your meeting</p>
              <p className="text-foreground-secondary type-ui-sm">
                Highlight can transcribe your meetings and understand who is talking.
              </p>
            </div>
            <div className="divide-border corner-smooth flex flex-col divide-y rounded-xl shadow-sm">
              <PermissionToggleRow label="Allow voice transcripts" checked={mic.isAuthorized} onRequest={mic.request} />
              {needs.systemAudio && (
                <PermissionToggleRow
                  label={systemAudioActionLabel(systemAudio.actionKind, 'Allow system audio transcripts', 'row')}
                  checked={systemAudio.isAuthorized}
                  onRequest={systemAudio.request}
                  disabled={nextPermission === 'microphone'}
                />
              )}
              {needs.screenRecording && (
                <PermissionToggleRow
                  label={screenRecordingActionLabel(screenRecording.actionKind, 'Allow screen recording', 'row')}
                  checked={screenRecording.isAuthorized}
                  onRequest={screenRecording.request}
                  disabled={nextPermission === 'microphone'}
                />
              )}
            </div>
          </div>
        </motion.div>
      )}
      {surface === 'ready' && (
        <motion.div key="ready" {...swap}>
          <div className="bg-background corner-smooth mb-3 flex flex-col gap-1.5 rounded-2xl border p-3">
            <p className="text-foreground type-ui-sm font-medium">Highlight is ready for your meetings</p>
            <p className="text-foreground-secondary type-ui-sm">
              Highlight will automatically transcribe your meetings and understand who is talking.
            </p>
          </div>
        </motion.div>
      )}
      {surface === 'notice' && (
        <motion.div key="notice" {...swap}>
          <div className="px-2 pb-1 pt-2">
            <MeetingModeNotice />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

interface PermissionToggleRowProps {
  label: string
  checked: boolean
  onRequest: () => void | Promise<void>
  disabled?: boolean
}

function PermissionToggleRow({ label, checked, onRequest, disabled }: PermissionToggleRowProps) {
  return (
    <div className={twMerge('flex items-center justify-between p-3', disabled && 'pointer-events-none opacity-25')}>
      <span className="text-foreground type-ui-sm font-medium">{label}</span>
      <PermissionSwitch
        checked={checked}
        disabled={disabled}
        ariaLabel={label}
        onCheckedChange={(next) => {
          if (next && !checked) {
            void onRequest()
          }
        }}
      />
    </div>
  )
}

interface PermissionSwitchProps {
  checked: boolean
  disabled?: boolean
  ariaLabel: string
  onCheckedChange: (checked: boolean) => void
}

/**
 * A permission-request button in switch clothing, not a stateful toggle: it
 * always reflects the live permission status and only ever fires a request
 * on the on-transition (see `PermissionToggleRow`).
 */
function PermissionSwitch({ checked, disabled, ariaLabel, onCheckedChange }: PermissionSwitchProps) {
  return (
    <Toggle
      checked={checked}
      disabled={disabled}
      aria-label={ariaLabel}
      onCheckedChange={onCheckedChange}
      // Off reads as missing, so the track is red until the grant lands.
      className="bg-danger/30 hover:bg-danger/40 active:bg-danger/50"
    />
  )
}
